import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

type OutcomeTone = "success" | "warning" | "error" | "info";

interface OutcomeStateProps {
  eyebrow: string;
  title: React.ReactNode;
  description: React.ReactNode;
  icon: LucideIcon;
  tone?: OutcomeTone;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

const toneClasses: Record<
  OutcomeTone,
  { iconWrap: string; icon: string; panel: string }
> = {
  success: {
    iconWrap: "bg-success/12",
    icon: "text-success",
    panel: "bg-success-light/70 text-success",
  },
  warning: {
    iconWrap: "bg-warning/12",
    icon: "text-warning",
    panel: "bg-warning-light/80 text-warning",
  },
  error: {
    iconWrap: "bg-destructive/12",
    icon: "text-destructive",
    panel: "bg-destructive/8 text-destructive",
  },
  info: {
    iconWrap: "bg-primary/12",
    icon: "text-primary",
    panel: "bg-primary/8 text-primary",
  },
};

export function OutcomeState({
  eyebrow,
  title,
  description,
  icon: Icon,
  tone = "info",
  meta,
  actions,
  className,
}: OutcomeStateProps) {
  const styles = toneClasses[tone];

  return (
    <div className={cn("min-h-screen premium-page px-4 py-8", className)}>
      <div className="mx-auto max-w-3xl">
        <div className="rounded-[2rem] border border-white/70 bg-white/88 p-8 text-center shadow-[var(--hero-shadow)] backdrop-blur">
          <div
            className={cn(
              "mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-3xl",
              styles.iconWrap
            )}
          >
            <Icon className={cn("h-8 w-8", styles.icon)} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {eyebrow}
          </p>
          <h1 className="mt-3 text-3xl font-semibold font-display text-foreground">
            {title}
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            {description}
          </p>

          {meta ? (
            <div className={cn("mt-6 rounded-2xl px-4 py-4 text-sm", styles.panel)}>
              {meta}
            </div>
          ) : null}

          {actions ? <div className="mt-6 flex flex-wrap justify-center gap-3">{actions}</div> : null}
        </div>
      </div>
    </div>
  );
}
